const SPORTS_PAGE_STATE_KEY = 'alp_sports_page_state_v1';
const PENDING_INPLAY_BET_KEY = 'alp_sports_pending_inplay_bet_v1';
const DEFAULT_BETTING_TYPE = 'DOMESTIC';
const VALID_BETTING_TYPES = new Set(['DOMESTIC', 'INTERNATIONAL', 'SPECIAL', 'LIVE']);
const MAX_PENDING_INPLAY_RESUME_MS = 10 * 60 * 1000;

const getAvailableStorage = () => {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage;
  } catch (_) {}

  try {
    return window.localStorage;
  } catch (_) {}

  return null;
};

const readJson = (key) => {
  const storage = getAvailableStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

const writeJson = (key, value) => {
  const storage = getAvailableStorage();
  if (!storage) return;

  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (_) {}
};

const removeItem = (key) => {
  const storage = getAvailableStorage();
  if (!storage) return;

  try {
    storage.removeItem(key);
  } catch (_) {}
};

const normalizeBettingType = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  return VALID_BETTING_TYPES.has(normalized) ? normalized : null;
};

const normalizeNumericId = (value) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
};

export const getSportsRouteSportId = (search = '') => {
  try {
    const params = new URLSearchParams(search);
    const sportId = normalizeNumericId(params.get('sport_id') || params.get('sportId'));
    return sportId ? Math.floor(sportId) : null;
  } catch (_) {
    return null;
  }
};

export const resolveLiveRouteSportFilter = (search = '', categories = []) => {
  const sportId = getSportsRouteSportId(search);
  if (!sportId) return { sportId: null, sport: null, resolved: true };

  const category = (Array.isArray(categories) ? categories : [])
    .find((row) => Number(row?.id) === sportId);
  const slug = String(category?.slug || '').trim();
  return {
    sportId,
    sport: slug ? { slug } : null,
    resolved: Boolean(slug),
  };
};

const normalizeTimestampMs = (value) => {
  if (!value) return null;

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) {
    return numeric;
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

export const readSportsPageState = () => {
  const raw = readJson(SPORTS_PAGE_STATE_KEY);
  if (!raw || typeof raw !== 'object') return null;

  return {
    bettingType: normalizeBettingType(raw.bettingType) || DEFAULT_BETTING_TYPE,
    selectedSportId: normalizeNumericId(raw.selectedSportId),
    popularMode: Boolean(raw.popularMode),
  };
};

export const writeSportsPageState = ({
  bettingType,
  selectedSportId,
  popularMode,
}) => {
  writeJson(SPORTS_PAGE_STATE_KEY, {
    bettingType: normalizeBettingType(bettingType) || DEFAULT_BETTING_TYPE,
    selectedSportId: normalizeNumericId(selectedSportId),
    popularMode: Boolean(popularMode),
  });
};

export const buildInitialSelectedSport = (selectedSportId) => {
  const normalizedId = normalizeNumericId(selectedSportId);
  if (!normalizedId) return null;

  return {
    id: normalizedId,
    slug: '',
    name: '',
    name_ko: '',
    icon_url: '',
  };
};

export const persistPendingInplayBet = ({
  betId,
  delaySeconds = 0,
  delayExpiresAt = null,
  selections = [],
  stake = '',
  bettingCategory = 'LIVE',
}) => {
  const normalizedBetId = normalizeNumericId(betId);
  if (!normalizedBetId) return;

  const createdAtMs = Date.now();
  const normalizedDelaySeconds = Math.max(Math.ceil(Number(delaySeconds) || 0), 0);
  const delayExpiresAtMs = normalizeTimestampMs(delayExpiresAt)
    || (createdAtMs + (normalizedDelaySeconds * 1000));

  writeJson(PENDING_INPLAY_BET_KEY, {
    betId: normalizedBetId,
    bettingCategory: normalizeBettingType(bettingCategory) || 'LIVE',
    delaySeconds: normalizedDelaySeconds,
    delayExpiresAtMs,
    createdAtMs,
    stake: stake === '' || stake == null ? '' : String(stake),
    selections: Array.isArray(selections) ? selections : [],
  });
};

export const clearPendingInplayBet = () => {
  removeItem(PENDING_INPLAY_BET_KEY);
};

export const readPendingInplayBet = () => {
  const raw = readJson(PENDING_INPLAY_BET_KEY);
  if (!raw || typeof raw !== 'object') return null;

  const betId = normalizeNumericId(raw.betId);
  if (!betId) {
    clearPendingInplayBet();
    return null;
  }

  const bettingCategory = normalizeBettingType(raw.bettingCategory) || 'LIVE';
  const createdAtMs = normalizeTimestampMs(raw.createdAtMs) || Date.now();
  const ageMs = Date.now() - createdAtMs;
  if (ageMs > MAX_PENDING_INPLAY_RESUME_MS) {
    clearPendingInplayBet();
    return null;
  }

  const delaySeconds = Math.max(Math.ceil(Number(raw.delaySeconds) || 0), 0);
  const delayExpiresAtMs = normalizeTimestampMs(raw.delayExpiresAtMs)
    || (createdAtMs + (delaySeconds * 1000));
  const secondsLeft = Math.max(Math.ceil((delayExpiresAtMs - Date.now()) / 1000), 0);

  return {
    betId,
    bettingCategory,
    delaySeconds,
    delayExpiresAtMs,
    secondsLeft,
    createdAtMs,
    stake: raw.stake === '' || raw.stake == null ? '' : String(raw.stake),
    selections: Array.isArray(raw.selections) ? raw.selections : [],
  };
};
